import axios from 'axios';
import Cookies from 'js-cookie';

const API_URL = process.env.REACT_APP_API_KEY;

// Setup Axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const apis = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const HandleLogout = async () => {

  await Cookies.remove('tokenAccess');
  await Cookies.remove('tokenRefresh');

};


// Token validity check
const isTokenValid = (token) => {
  const decodedToken = JSON.parse(atob(token.split('.')[1]));
  const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
  return decodedToken.exp > currentTime; // Compare expiration time with current time
};

// Check if token is expiring soon
const TOKEN_REFRESH_THRESHOLD = 60; 

const isTokenExpiringSoon = (token) => {
  const decodedToken = JSON.parse(atob(token.split('.')[1]));
  const currentTime = Math.floor(Date.now() / 1000);
  return decodedToken.exp - currentTime < TOKEN_REFRESH_THRESHOLD;
};

// Refresh the access token
const refreshAccessToken = async () => {
  const tokenRefresh = Cookies.get('tokenRefresh');
  if (tokenRefresh) {
    try {
      const response = await apis.post('/auth/token/refresh/', { refresh: tokenRefresh });
      if (response.status === 200) {
        Cookies.set('tokenAccess', response.data.access, { expires: 1 });
      }
    } catch (error) {

      HandleLogout();
    }
  }
};

// Request Interceptor
api.interceptors.request.use(
  async (config) => {
    const tokenAccess = Cookies.get('tokenAccess');
    const tokenRefresh = Cookies.get('tokenRefresh');

    if (tokenAccess) {
      if (isTokenValid(tokenAccess)) {
        // Access token is valid, check if it's expiring soon
        if (isTokenExpiringSoon(tokenAccess)) {
          await refreshAccessToken(); // Refresh the access token
        }
        config.headers['Authorization'] = `Bearer ${Cookies.get('tokenAccess')}`;
        return config; // Proceed with the request
      } else {
        // Access token is expired, attempt to refresh
        if (tokenRefresh) {
          await refreshAccessToken(); // Try to refresh the token
          const newAccessToken = Cookies.get('tokenAccess');
          
          if (newAccessToken) {
            config.headers['Authorization'] = `Bearer ${newAccessToken}`; // Retry original request with new access token
            return config; // Proceed with the request
          } else {
            // Refresh failed; log out
            HandleLogout();
            return Promise.reject("Both tokens are expired. User logged out.");
          }
        } else {
          // No refresh token available; log out
          HandleLogout();
          return Promise.reject("No refresh token available. User logged out.");
        }
      }
    } else {
      // No access token available; log out
      HandleLogout();
      return Promise.reject("No access token available. User logged out.");
    }

    
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor
api.interceptors.response.use(
  (response) => {
    return response; // Return the successful response
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const tokenRefresh = Cookies.get('tokenRefresh');
      const tokenAccess = Cookies.get('tokenAccess');

      // Check if the access token is present
      if (!isTokenValid(tokenAccess) || isTokenExpiringSoon(tokenAccess)) {
        //console.warn("Access token missing, logging out.");
        HandleLogout(); // Logout if no access token is found
        return Promise.reject("No access token available. User logged out.");
      }

      // Attempt to refresh the access token
      if (!tokenRefresh) {
        //console.warn("No refresh token available, logging out.");
        HandleLogout(); // Logout if no refresh token is found
        return Promise.reject("No refresh token available. User logged out.");
      }

      try {
        const response = await apis.post('/auth/token/refresh/', { refresh: tokenRefresh });

        if (response.status === 200) {
          // Successful refresh
          Cookies.set('tokenAccess', response.data.access, { expires: 1 });
          originalRequest.headers['Authorization'] = `Bearer ${response.data.access}`;
          return api(originalRequest); // Retry original request with new access token
        } else {
          // If the response is not successful, handle logout
          //console.warn("Refresh token error: logging out.");
          HandleLogout(); // Logout if refresh response is not successful
          return Promise.reject("Refresh token request failed. User logged out.");
        }
      } catch (refreshError) {
        //console.error("Refresh token failed:", refreshError);
        //console.warn("Refresh token failed, logging out.");
        HandleLogout(); // Logout if refresh fails
        return Promise.reject("Refresh token failed. User logged out."); // Reject with specific message
      }
    }

    return Promise.reject(error); // For other errors, reject the promise
  }
);



export default api;